import { useCallback, useEffect } from 'react';
import { useAppDispatch } from '../base';
import {
    IPostConnectOutlookAction,
    organizationActions,
    userActions,
} from '../actions';
import {
    generateCodeChallenge,
    generateCodeVerifier,
} from '../common/utils/pkce';

export type OutlookContext = 'company' | 'user';

interface UseOutlookOptions {
    handleRedirect?: boolean;
}

const REDIRECT_PATHS: Record<OutlookContext, string> = {
    company: '/settings',
    user: '/user-settings',
};

const useOutlook = (
    context: OutlookContext = 'company',
    { handleRedirect = true }: UseOutlookOptions = {}
) => {
    const dispatch = useAppDispatch();
    const redirectPath = REDIRECT_PATHS[context];

    useEffect(() => {
        if (!handleRedirect) return;

        const urlParams = new URLSearchParams(window.location.search);
        const authCode = urlParams.get('code');
        const codeVerifier = sessionStorage.getItem('pkceCodeVerifier') || '';
        const storedContext = sessionStorage.getItem(
            'outlookConnectContext'
        ) as OutlookContext | null;

        if (authCode && codeVerifier && storedContext === context) {
            if (context === 'user') {
                dispatch(
                    userActions.postConnectUserOutlook({
                        code: authCode,
                        codeVerifier,
                        redirectPath,
                    })
                );
            } else {
                dispatch(
                    organizationActions.postConnectOutlook({
                        code: authCode,
                        codeVerifier,
                    } as IPostConnectOutlookAction)
                );
            }
            sessionStorage.removeItem('pkceCodeVerifier');
            sessionStorage.removeItem('outlookConnectContext');
            window.history.replaceState({}, document.title, redirectPath);
        }
    }, [dispatch, context, redirectPath, handleRedirect]);

    const connectOutlook = useCallback(async () => {
        try {
            const codeVerifier = generateCodeVerifier();
            sessionStorage.setItem('pkceCodeVerifier', codeVerifier);
            sessionStorage.setItem('outlookConnectContext', context);
            const codeChallenge = await generateCodeChallenge(codeVerifier);

            const authority = 'https://login.microsoftonline.com/common';
            const clientId = import.meta.env.VITE_AZURE_CLIENT_ID;
            const redirectUri = `${window.location.origin}${redirectPath}`;

            const scopes = [
                'https://graph.microsoft.com/Mail.ReadWrite',
                'https://graph.microsoft.com/Calendars.ReadWrite',
                'https://graph.microsoft.com/User.Read',
                'https://graph.microsoft.com/Mail.Send',
                'offline_access',
            ].join(' ');

            const state = 'custom_state_string';

            const params = new URLSearchParams({
                client_id: clientId,
                response_type: 'code',
                redirect_uri: redirectUri,
                scope: scopes,
                response_mode: 'query',
                prompt: 'select_account',
                code_challenge: codeChallenge,
                code_challenge_method: 'S256',
                state: state,
            });

            const authUrl = `${authority}/oauth2/v2.0/authorize?${params.toString()}`;

            window.location.assign(authUrl);
        } catch (error) {
            console.error('Authentication failed:', error);
        }
    }, [context, redirectPath]);

    const disconnectOutlook = useCallback(async () => {
        console.log('Disconnecting Outlook');
    }, []);

    return {
        connectOutlook,
        disconnectOutlook,
    };
};

export default useOutlook;
